import React, { useState, useEffect } from "react";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import EditIcon from "@material-ui/icons/Edit";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import DeleteIcon from "@material-ui/icons/Delete";
import {
  Checkbox,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Popper,
  Chip,
} from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { currentUserHasRole, Role } from "../../utils/RoleUtils";
import { useSelector, useDispatch } from "react-redux";
import { setDocumentCategories } from "../../redux/actions/universalActions";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { makeStyles } from "@material-ui/core/styles";
import axios from "../../AxiosClient";
import ActionButton from "../ActionButton";
import { connect } from "react-redux";

const useStyles = makeStyles(() => ({
  dialog: {
    display: "flex",
    flexDirection: "column",
  },
  buttonContainer: {
    textAlign: "center",
    marginBottom: 10,
  },
  chip: {
    marginBottom: 5,
  },
  input: {
    marginBottom: 3,
  },
  chipOnly: {
    margin: 1,
  },
}));

const filter = createFilterOptions();

function DocumentCategories(props) {
  const [categoriesSelected, setCategoriesSelected] = useState([]);
  const [categoryInput, setCategoryInput] = useState("");
  const [edit, setEdit] = useState(false);
  const [forEdit, setForEdit] = useState([]);
  const [toDelete, setToDelete] = useState([]);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const categories = useSelector((state) => state.prefs.documentCategories);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const styles = props.style ? { ...props.style } : {};
  const classes = useStyles();

  useEffect(() => {
    if (props.defaultValue) {
      setCategoriesSelected(
        categories.filter((c) => props.defaultValue.includes(c.id))
      );
    }
  }, []);

  useEffect(
    () => {
      if (props.defaultSelectedCategories) {
        let selected = [];
        categories.forEach((cat) => {
          props.defaultSelectedCategories.includes(cat.id) &&
            selected.push(cat);
        });
        setCategoriesSelected(selected);
      }
    },
    [props.defaultSelectedCategories]
  );

  useEffect(
    () => {
      if (categories) {
        getGroups();
      }
    },
    [categories]
  );

  useEffect(
    () => {
      if (edit) {
        setForEdit(JSON.parse(JSON.stringify(categories)));
      } else {
        setForEdit([]);
        setToDelete([]);
        getGroups();
      }
    },
    [edit]
  );

  const saveDocumentCategories = () => {
    setSaving(true);
    let promises = [
      axios.patch("/document-category/all", forEdit.filter((c) => !!c.title)),
    ];
    if (toDelete.length > 0) {
      promises.push(axios.post("/document-category/delete/all", [...toDelete]));
    }
    Promise.all(promises)
      .then(([saveRes, _deleteRes]) => {
        dispatch(setDocumentCategories(saveRes.data));
        enqueueSnackbar(t("document_categories_saved"), { variant: "success" });
        setSaving(false);
        setEdit(false);
      })
      .catch(() => {
        setSaving(false);
        enqueueSnackbar(t("error_occurred_generic"), { variant: "error" });
      });
  };

  const getGroups = () => {
    setGroups([
      ...new Set(
        categories.filter((c) => !!c.groupName).map((c) => c.groupName)
      ),
    ]);
  };

  const deleteCategory = (category, idx) => {
    let clone = [...forEdit];
    clone.splice(idx, 1);
    if (category.id) {
      setToDelete([...toDelete, category.id]);
    }
    setForEdit(clone);
  };

  const addCategory = () => {
    setForEdit([{ id: null, title: "", groupName: null }, ...forEdit]);
  };

  const editCategory = (category, idx) => {
    let clone = [...forEdit];
    clone.splice(idx, 1, category);
    setForEdit(clone);
  };

  const chipsOnly = () => {
    if (props.defaultValue == null) {
      return null;
    }
    if (props.defaultValue.length === 0) {
      return t("no_categories");
    }
    return categories
      .filter((cat) => props.defaultValue.includes(cat.id))
      .map((cat, idx) => {
        return (
          <Chip
            size="small"
            key={idx}
            label={cat.title}
            className={classes.chipOnly}
          />
        );
      });
  };

  if (props.chipsOnly) {
    return chipsOnly();
  }

  return (
    <>
      <div
        style={{
          width: "100%",
          minWidth: !props.isMobile && 300,
          ...styles,
        }}
      >
        <Autocomplete
          disabled={props.disabled}
          size="small"
          disablePortal={props.disablePortal}
          classes={{
            tag: classes.chip,
            input: classes.input,
          }}
          multiple
          disableCloseOnSelect
          handleHomeEndKeys
          fullWidth
          id="categories"
          openOnFocus
          options={categories}
          groupBy={
            groups.length > 0
              ? (opt) => {
                  if (opt.groupName || opt.inputValue === "EDIT") {
                    return opt.groupName;
                  }
                  return t("document_categories_other_group");
                }
              : undefined
          }
          value={categoriesSelected}
          onChange={(_e, val) => {
            let edit = val.filter((v) => v.inputValue === "EDIT");
            if (edit.length > 0) {
              setEdit(true);
            } else {
              setCategoriesSelected(val);
              props.onChange && props.onChange(val);
            }
          }}
          getOptionSelected={(op, val) => {
            return op.title === val.title;
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            if (currentUserHasRole(Role.MODERATOR)) {
              filtered.unshift({
                inputValue: "EDIT",
                title: t("document_categories_edit_categories"),
              });
            }
            return filtered;
          }}
          renderOption={(option, { selected }) => (
            <React.Fragment>
              {option.inputValue === "EDIT" ? (
                <EditIcon style={{ marginRight: 8 }} />
              ) : (
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
              )}
              {option.title}
            </React.Fragment>
          )}
          inputValue={categoryInput}
          onInputChange={(_e, val) => {
            setCategoryInput(val);
          }}
          getOptionLabel={(option) => option.title}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label={t("global_category_plural")}
            />
          )}
        />
      </div>
      {edit && (
        <Dialog open={edit} fullWidth maxWidth="md">
          <DialogTitle>
            {t("document_categories_edit_dialog_header")}
          </DialogTitle>
          <DialogContent className={classes.dialog}>
            <Typography gutterBottom variant="subtitle1">
              {t("document_categories_edit_dialog_body")}
            </Typography>
            <div className={classes.buttonContainer}>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={addCategory}
              >
                {t("create_document_category")}
              </Button>
            </div>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("document_category_name")}</TableCell>
                    <TableCell>{t("document_category_group")}</TableCell>
                    <TableCell>{t("global_actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {forEdit.map((category, idx) => {
                    return (
                      <TableRow key={idx}>
                        <TableCell>
                          <TextField
                            fullWidth
                            value={category.title}
                            onChange={(e) =>
                              editCategory(
                                { ...category, title: e.target.value },
                                idx
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            autoSelect
                            freeSolo
                            options={groups}
                            value={category.groupName}
                            onChange={(_e, val) =>
                              editCategory(
                                { ...category, groupName: val ? val : null },
                                idx
                              )
                            }
                            renderInput={(params) => <TextField {...params} />}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={t("document_category_delete")}>
                            <IconButton
                              onClick={() => {
                                deleteCategory(category, idx);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEdit(false)}>{t("global_cancel")}</Button>
            <ActionButton
              loading={saving}
              onClick={() => saveDocumentCategories()}
            >
              {t("global_save")}
            </ActionButton>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
const mapStateToProps = (state) => ({
  isMobile: state.screen.isMobile,
});

const mapDispatchToProps = {};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DocumentCategories);
